import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import { handleApiError } from "../../pages/UBTAdmin/ProjectDetails/ProjectHelper/ViewProjectHandleApiError";

export interface TestDataSeriesItem {
  [key: string]: any;
}

export const DATE_FORMATS = {
  SLASH: "MM/DD/YYYY",
  DASH: "MM-DD-YYYY",
  API: "YYYY-MM-DD HH:MM:SS",
} as const;

export interface TestDataSeriesState {
  testDataSeriesItems: TestDataSeriesItem[];
  testDataSeriesLoading: boolean;
  testDataSeriesError: string | null;
  addTestDataSeriesLoading: boolean;
  addTestDataSeriesError: string | null;
  updateTestDataSeriesLoading: boolean;
  updateTestDataSeriesError: string | null;
  testDataSeriesCurrentPage: number;
  testDataSeriesPerPage: number;
  testDataSeriesTotalItems: number;
  testDataSeriesTotalPages: number;
}

const initialState: TestDataSeriesState = {
  testDataSeriesItems: [],
  testDataSeriesLoading: false,
  testDataSeriesError: null,
  addTestDataSeriesLoading: false,
  addTestDataSeriesError: null,
  updateTestDataSeriesLoading: false,
  updateTestDataSeriesError: null,
  testDataSeriesCurrentPage: 1,
  testDataSeriesPerPage: 5,
  testDataSeriesTotalItems: 0,
  testDataSeriesTotalPages: 0,
};

export const updateTestDataSeries = createAsyncThunk(
  "ubtPortal/updateTestDataSeries",
  async (
    { tocId, seriesId, payload, nuid }: { tocId: number; seriesId: number; payload: any; nuid?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const options: any = {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.testDataSeries.updateTestDataSeries === "function"
          ? endpoints.ubt.testDataSeries.updateTestDataSeries(tocId, seriesId)
          : endpoints.ubt.testDataSeries.updateTestDataSeries;
      const response = await api.patch(url, payload, options);
      return { seriesId, data: response.data };
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to update test data series");
    }
  },
);

export const fetchTestDataSeries = createAsyncThunk(
  "ubtPortal/fetchTestDataSeries",
  async (
    {
      tocId,
      sortColumn,
      sortOrder,
      page,
      perPage,
      nuid,
    }: {
      tocId: number;
      sortColumn?: string;
      sortOrder?: string;
      page?: number;
      perPage?: number;
      nuid?: string | null;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const params: any = {};
      if (sortColumn) params.sortColumn = sortColumn;
      if (sortOrder) params.sortOrder = sortOrder;
      if (page != null) params.page = page;
      if (perPage != null) params.per_page = perPage;

      const options: any = {
        signal,
        params,
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.testDataSeries.getTestDataSeries === "function"
          ? endpoints.ubt.testDataSeries.getTestDataSeries(tocId)
          : endpoints.ubt.testDataSeries.getTestDataSeries;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to fetch test data series");
    }
  },
);


export const addTestDataSeries = createAsyncThunk(
  "ubtPortal/addTestDataSeries",
  async (
    {
      tocId,
      payload,
      nuid,
    }: { tocId: number; payload: any; nuid?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const options: any = {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.testDataSeries.addTestDataSeries === "function"
          ? endpoints.ubt.testDataSeries.addTestDataSeries(tocId)
          : endpoints.ubt.testDataSeries.addTestDataSeries;

      const response = await api.post(url, payload, options);
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to add test data series");
    }
  },
);

export const deleteTestDataSeries = createAsyncThunk(
  "ubtPortal/deleteTestDataSeries",
  async (
    { tocId, seriesId, nuid }: { tocId: number; seriesId: number; nuid?: string | null },
    { rejectWithValue },
  ) => {
    try {
      const options: any = {
        headers: {
          accept: "application/json",
          ...(nuid ? { "x-nuid": nuid } : {}),
        },
      };

      const url =
        typeof endpoints.ubt.testDataSeries.deleteTestDataSeries === "function"
          ? endpoints.ubt.testDataSeries.deleteTestDataSeries(tocId, seriesId)
          : endpoints.ubt.testDataSeries.deleteTestDataSeries;

      const response = await api.delete(url, options);
      return { seriesId, data: response.data };
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to delete test data series");
    }
  },
);

const testDataSeriesSlice = createSlice({
  name: "testDataSeries",
  initialState,
  reducers: {
    resetTestDataSeriesState: (state) => {
      state.testDataSeriesItems = [];
      state.testDataSeriesLoading = false;
      state.testDataSeriesError = null;
      state.addTestDataSeriesLoading = false;
      state.addTestDataSeriesError = null;
      state.testDataSeriesCurrentPage = 1;
      state.testDataSeriesTotalItems = 0;
      state.testDataSeriesTotalPages = 0;
    },
    setTestDataSeriesCurrentPage: (state, action: PayloadAction<number>) => {
      state.testDataSeriesCurrentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTestDataSeries.pending, (state) => {
      state.testDataSeriesLoading = true;
      state.testDataSeriesError = null;
    });

    builder.addCase(fetchTestDataSeries.fulfilled, (state, action) => {
      state.testDataSeriesLoading = false;
      const payload: any = action.payload;

      if (payload?.items && Array.isArray(payload.items)) {
        state.testDataSeriesItems = payload.items;
        state.testDataSeriesTotalItems = payload.meta?.total ?? state.testDataSeriesTotalItems;
        state.testDataSeriesTotalPages = payload.meta?.totalPages ?? state.testDataSeriesTotalPages;
        state.testDataSeriesCurrentPage = payload.meta?.pageNumber ?? state.testDataSeriesCurrentPage;
        state.testDataSeriesPerPage = payload.meta?.pageSize ?? state.testDataSeriesPerPage;
      } else if (Array.isArray(payload)) {
        state.testDataSeriesItems = payload;
        state.testDataSeriesTotalItems = payload.length;
        state.testDataSeriesTotalPages = 1;
      } else if (payload?.data && Array.isArray(payload.data)) {
        state.testDataSeriesItems = payload.data;
        state.testDataSeriesTotalItems = payload.total ?? state.testDataSeriesTotalItems;
      } else {
        state.testDataSeriesItems = [];
      }
    });

    builder.addCase(fetchTestDataSeries.rejected, (state, action) => {
      state.testDataSeriesLoading = false;
      state.testDataSeriesError = action.payload as string;
    });

    builder.addCase(addTestDataSeries.pending, (state) => {
      state.addTestDataSeriesLoading = true;
      state.addTestDataSeriesError = null;
    });

    builder.addCase(addTestDataSeries.fulfilled, (state, action) => {
      state.addTestDataSeriesLoading = false;
      const payload: any = action.payload;
      const created = payload?.data ?? payload;

      if (created) {
        if (Array.isArray(created)) {
          state.testDataSeriesItems = [...created, ...state.testDataSeriesItems];
          state.testDataSeriesTotalItems = (state.testDataSeriesTotalItems ?? 0) + created.length;
        } else {
          state.testDataSeriesItems = [created, ...state.testDataSeriesItems];
          state.testDataSeriesTotalItems = (state.testDataSeriesTotalItems ?? 0) + 1;
        }
      }
    });

    builder.addCase(addTestDataSeries.rejected, (state, action) => {
      state.addTestDataSeriesLoading = false;
      state.addTestDataSeriesError = action.payload as string;
    });

    builder.addCase(updateTestDataSeries.pending, (state) => {
      state.updateTestDataSeriesLoading = true;
      state.updateTestDataSeriesError = null;
    });

    builder.addCase(updateTestDataSeries.fulfilled, (state, action) => {
      state.updateTestDataSeriesLoading = false;
      const payload: any = action.payload;
      const seriesId = payload?.seriesId ?? (payload?.data?.seriesId ?? null);
      const updated = payload?.data ?? payload;

      if (seriesId != null) {
        state.testDataSeriesItems = state.testDataSeriesItems.map((it: any) =>
          (it.seriesId ?? it.id) === seriesId ? (updated?.data ?? updated) : it,
        );
      } else if (updated) {
        const matchId = updated.seriesId ?? updated.id;
        if (matchId != null) {
          state.testDataSeriesItems = state.testDataSeriesItems.map((it: any) =>
            (it.seriesId ?? it.id) === matchId ? updated : it,
          );
        }
      }
    });

    builder.addCase(updateTestDataSeries.rejected, (state, action) => {
      state.updateTestDataSeriesLoading = false;
      state.updateTestDataSeriesError = action.payload as string;
    });

    builder.addCase(deleteTestDataSeries.pending, (state) => {
      state.testDataSeriesError = null;
    });

    builder.addCase(deleteTestDataSeries.fulfilled, (state, action) => {
      const payload: any = action.payload;
      const seriesId = payload?.seriesId ?? null;
      if (seriesId != null) {
        state.testDataSeriesItems = state.testDataSeriesItems.filter((it: any) => (it.seriesId ?? it.id) !== seriesId);
        state.testDataSeriesTotalItems = Math.max(0, (state.testDataSeriesTotalItems ?? 0) - 1);
      }
    });

    builder.addCase(deleteTestDataSeries.rejected, (state, action) => {
      state.testDataSeriesError = action.payload as string;
    });
  },
});

export const { resetTestDataSeriesState, setTestDataSeriesCurrentPage } = testDataSeriesSlice.actions;
export default testDataSeriesSlice.reducer;
